import { Icon } from '@rsuite/icons';
import React, { useEffect, useRef, useState } from 'react';
import { MdOutlinePhotoSizeSelectActual } from 'react-icons/md';
import { Loader } from 'rsuite';
import { AiOutlineCloseCircle, AiOutlineDownload } from 'react-icons/ai'; // Add a close (cross) icon from react-icons
import { ObjectStorageFileCategory, ObjectStorageFileType } from '@/models/storage.model';
import { AxiosError } from 'axios';
import { BaseErrorModel } from '@/models/core.model';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { uploadToStorage_v2 } from '@/services/storage.service';
import { IconProps } from '@rsuite/icons/esm/Icon';

export interface FileChangeEvent extends React.SyntheticEvent<Element, Event> {
  fileName?: string;
  fileExtension?: string;
}

// Define the prop types for ImageUploader
interface ImageUploaderProps {
    objectStorageCategory: ObjectStorageFileCategory; // Pass the category for the file
    onChange?: (value: any, event?: FileChangeEvent, extraData?: any) => void; // Callback to pass the URL or undefined
    url?: string;
    defaultIcon?: React.ElementType<IconProps>; // Optional icon component (e.g., from react-icons or rsuite)
    height?: string; // Optional height for the button
    width?: string; // Optional width for the button
    downloadable?: boolean
    uploadable?: boolean;
    clearable?: boolean;
    isCompressed?: boolean;
    accept?: string;
    disabled?: boolean;
  }

  const ImageUploader: React.FC<ImageUploaderProps> = ({
    objectStorageCategory,
    onChange,
    url,
    defaultIcon: defaultIcon = MdOutlinePhotoSizeSelectActual,
    height = '300px', // Default height
    width = '100%', // Default width
    downloadable = false,
    uploadable = true,
    clearable = true,
    accept = ".jpg,.jpeg,.png",
    disabled = false
  }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [imageUrl, setImageUrl] = useState<string | undefined>(''); // Store the uploaded image URL

    useEffect(()=> {
      setImageUrl(url);
    },[url]);
    
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        setUploading(true);
        const file = e.target.files[0];
        uploadToStorage_v2({file: file, category: objectStorageCategory, type: ObjectStorageFileType.Image})          
          .then(data => {
            console.log('Uploaded URL:', data);
            setImageUrl(data.url); // Update internal state
            const changeEvent = {...e, fileName: data.fileName, fileExtension: data.fileExtension};
            onChange && onChange(data.url, changeEvent); // Notify the parent of the uploaded URL
          })
          .catch(err => {
            const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
            toastErrorMessage(`Error in uploading image: ${errorModel?.errorMessage}`);
            fileInputRef.current!.value = '';
          })
          .finally(() => {
            setUploading(false);
          });
      } else {
        setImageUrl(undefined); // Clear the image if no file is selected
        onChange && onChange(undefined); // Notify parent of clearing
      }
    };
    
    const triggerFileInput = () => {
      if (uploadable && fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const clearImage = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      setImageUrl(undefined);
      onChange && onChange(undefined); // Notify parent that the image is cleared
    };

    const downloadImage = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, url: string) => {
      e.stopPropagation();

      fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        // Extract the filename from the URL path
        const urlPath = new URL(url).pathname; // Extracts only the path part of the URL
        const filename = urlPath.substring(urlPath.lastIndexOf('/') + 1) || 'downloaded-file';
  
        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename; // Use the extracted filename
        document.body.appendChild(link);
  
        link.click();
  
        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => {
        console.error('Error fetching the file:', error);
      });
    };

  return (
    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept={accept}
      />
      <button
        style={{
          width: width,
          height: height,
          border: '1px solid #ccc',
          cursor: uploadable ? 'pointer' : 'default',
          position: 'relative',
          borderRadius: 10
        }}
        onClick={triggerFileInput}
        aria-label="Upload Image"
        disabled = {disabled}
      >
        {uploading && <Loader backdrop center />}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Uploaded"
            style={{
              maxWidth: '95%',
              maxHeight: '95%',
              objectFit: 'contain'
            }}
          />
        ) : (
            defaultIcon && <Icon as={defaultIcon} style={{ fontSize: 80 }} /> 
        )}
        {imageUrl && (
          <>
            {/* Clear Image Button */}
            {clearable && !disabled && <span
              onClick={clearImage}
              style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                fontSize: '24px',
                color: 'darkred',
                cursor: 'pointer',
                zIndex: 1,
              }}
            >
              <AiOutlineCloseCircle />
            </span>}
            
            {/* Download Button */}
            { downloadable && !disabled && <span
              onClick={e => downloadImage(e, imageUrl)}
              style={{
                position: 'absolute',
                bottom: '5px',
                right: '5px',
                fontSize: '24px',
                color: 'darkblue',
                cursor: 'pointer',
                zIndex: 1,
              }}
              aria-label="Download Image"
            >
              <AiOutlineDownload />
            </span> }     
          </>
        )}
      </button>
    </div>
  );
};

export default ImageUploader;